import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import type { Document, DocumentFormValues } from "../models/document"
import { DocumentSchema } from "../models/document"
import { documentApi } from "../services/documentApi"
import { useCrudList, useCrudItem } from "../../../hooks/useCrud"

const BASE_KEY = "documents"
const BASE_URL = "document"

export function useDocuments(filters?: Record<string, unknown>) {
  return useCrudList<Document>(BASE_KEY, BASE_URL, DocumentSchema, filters)
}

export function useDocument(id?: number | null) {
  return useCrudItem<Document>(BASE_KEY, BASE_URL, DocumentSchema, id)
}

export function useDocumentCategories() {
  return useQuery({
    queryKey: [BASE_KEY, "categories"],
    queryFn: () => documentApi.getCategories(),
    staleTime: 1000 * 60 * 30,
  })
}

export function useDocumentTypes() {
  return useQuery({
    queryKey: [BASE_KEY, "types"],
    queryFn: () => documentApi.getTypes(),
    staleTime: 1000 * 60 * 30,
  })
}

export function useCreateDocument() {
  const queryClient = useQueryClient()
  return useMutation<Document, Error, DocumentFormValues & { file?: File }>({
    mutationFn: (values) => documentApi.create(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useUpdateDocument() {
  const queryClient = useQueryClient()
  return useMutation<Document, Error, { id: number; values: DocumentFormValues & { file?: File | null } }>({
    mutationFn: ({ id, values }) => documentApi.update(id, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, number>({
    mutationFn: (id) => documentApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_KEY] })
    },
  })
}
